<template>
  <Menu v-slot="{ open: isMenuOpen }" as="div" class="relative">
    <MenuOpenSync :open="isMenuOpen" @update:open="menuOpen = $event" />
    <div
      ref="filterRowRef"
      class="flex items-center gap-2 px-4 py-3"
      role="group"
      aria-label="Search filters"
    >
      <div
        ref="measureRowRef"
        class="pointer-events-none invisible absolute flex gap-2"
        data-testid="global-search-filter-measure"
        aria-hidden="true"
      >
        <span
          v-for="chip in filterChips"
          :key="`measure-${chip.key}`"
          data-filter-chip
          :class="filterChipClass"
        >
          <span
            v-if="modelValue === chip.key"
            :class="selectedIndicatorClass"
          />
          <component
            :is="chipIcon(chip)?.component"
            v-if="chipIcon(chip)"
            :class="['size-3.5 shrink-0', chipIcon(chip)?.colorClass]"
            aria-hidden="true"
          />
          <span>{{ chip.label }}</span>
          <span v-if="chip.count !== null" :class="filterChipCountClass">
            {{ chip.count }}
          </span>
        </span>
        <span
          ref="measureOverflowRef"
          data-testid="global-search-filter-overflow-measure"
          :class="overflowButtonClass"
        >
          <MenuMoreHorizontalIcon class="size-3.5" />
        </span>
      </div>

      <button
        v-for="chip in visibleFilterChips"
        :key="chip.key"
        type="button"
        :class="[
          filterChipClass,
          'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-fp-gray-5/40',
          modelValue === chip.key ? 'ring-1 ring-primary-5' : '',
        ]"
        :disabled="isFilterDisabled(chip)"
        :aria-pressed="modelValue === chip.key"
        @click="emit('update:modelValue', chip.key)"
      >
        <span
          v-if="modelValue === chip.key"
          :class="selectedIndicatorClass"
          data-selected-indicator
          aria-hidden="true"
        />
        <component
          :is="chipIcon(chip)?.component"
          v-if="chipIcon(chip)"
          :class="['size-3.5 shrink-0', chipIcon(chip)?.colorClass]"
          aria-hidden="true"
        />
        <span>{{ chip.label }}</span>
        <span v-if="chip.count !== null" :class="filterChipCountClass">
          {{ chip.count }}
        </span>
      </button>

      <MenuButton
        v-show="overflowFilterChips.length > 0"
        type="button"
        :class="[
          overflowButtonClass,
          'relative z-20 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-fp-gray-5/40',
          isMenuOpen || overflowContainsSelection
            ? 'ring-1 ring-primary-5'
            : '',
        ]"
        aria-label="More filters"
      >
        <MenuMoreHorizontalIcon
          class="size-3.5 text-fp-gray-5"
          aria-hidden="true"
        />
      </MenuButton>
      <MenuItems
        class="absolute right-4 top-full z-20 mt-1 flex max-h-[24rem] flex-col items-end gap-2 overflow-y-auto bg-fp-gray-1 p-2 pb-4 focus:outline-hidden overscroll-y-contain"
      >
        <MenuItem
          v-for="chip in overflowFilterChips"
          :key="chip.key"
          as="template"
          :disabled="isFilterDisabled(chip)"
        >
          <button
            type="button"
            :class="[
              filterChipClass,
              modelValue === chip.key ? 'ring-1 ring-primary-5' : '',
            ]"
            :disabled="isFilterDisabled(chip)"
            :aria-pressed="modelValue === chip.key"
            @click="emit('update:modelValue', chip.key)"
          >
            <span
              v-if="modelValue === chip.key"
              :class="selectedIndicatorClass"
              data-selected-indicator
              aria-hidden="true"
            />
            <component
              :is="chipIcon(chip)?.component"
              v-if="chipIcon(chip)"
              :class="['size-3.5 shrink-0', chipIcon(chip)?.colorClass]"
              aria-hidden="true"
            />
            <span>{{ chip.label }}</span>
            <span v-if="chip.count !== null" :class="filterChipCountClass">
              {{ chip.count }}
            </span>
          </button>
        </MenuItem>
      </MenuItems>
    </div>
  </Menu>
</template>

<script lang="ts" setup>
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import MenuMoreHorizontalIcon from '~/../member-webapp-shared/components/icons/streamline/MenuMoreHorizontalIcon.vue'
import { splitItemsForOverflow } from '~/utils/splitItemsForOverflow'
import {
  globalSearchRecordTypeIcon,
  GLOBAL_SEARCH_TOOLS_ICON,
} from '~/utils/globalSearchLinks'
import { GlobalSearchRecordTypeEnum } from '~~/models/graphql/generated/types'

export type SearchFilter = 'top' | 'tools' | GlobalSearchRecordTypeEnum

type FilterChip = {
  key: SearchFilter
  label: string
  count: number | null
}

const FILTER_ROW_GAP = 8
const filterChipClass =
  'inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-fp-gray-2/50 py-1 pl-2 pr-1 text-xs-semibold text-fp-gray-5 disabled:cursor-not-allowed disabled:opacity-40'
const filterChipCountClass =
  'inline-flex size-5.5 items-center justify-center rounded-full bg-fp-gray-2 text-xs-med text-fp-gray-5'
const selectedIndicatorClass = 'size-2 shrink-0 rounded-full bg-fp-green-5'
const overflowButtonClass =
  'inline-flex h-7 shrink-0 items-center justify-center rounded-full bg-fp-gray-2 px-2'

const RECORD_FILTER_CHIPS: {
  key: GlobalSearchRecordTypeEnum
  label: string
}[] = [
  { key: GlobalSearchRecordTypeEnum.Grant, label: 'Grants' },
  { key: GlobalSearchRecordTypeEnum.Contribution, label: 'Contributions' },
  { key: GlobalSearchRecordTypeEnum.Fees, label: 'Fees' },
  { key: GlobalSearchRecordTypeEnum.CaseRecord, label: 'Other Cases' },
  { key: GlobalSearchRecordTypeEnum.Contact, label: 'Contacts' },
  { key: GlobalSearchRecordTypeEnum.Charity, label: 'Charities' },
  { key: GlobalSearchRecordTypeEnum.Fund, label: 'Funds' },
  { key: GlobalSearchRecordTypeEnum.User, label: 'Users' },
  { key: GlobalSearchRecordTypeEnum.ProvisionalPledge, label: 'Pledges' },
  {
    key: GlobalSearchRecordTypeEnum.AmendedPledge,
    label: 'Amended Pledges',
  },
  { key: GlobalSearchRecordTypeEnum.Company, label: 'Companies' },
  { key: GlobalSearchRecordTypeEnum.Employee, label: 'Employees' },
  { key: GlobalSearchRecordTypeEnum.Role, label: 'Roles' },
  { key: GlobalSearchRecordTypeEnum.Event, label: 'Events' },
  {
    key: GlobalSearchRecordTypeEnum.TrainingModule,
    label: 'Training Modules',
  },
  { key: GlobalSearchRecordTypeEnum.Policy, label: 'Policies' },
  { key: GlobalSearchRecordTypeEnum.NewsItem, label: 'News' },
  {
    key: GlobalSearchRecordTypeEnum.TargetAudience,
    label: 'Target Audiences',
  },
  { key: GlobalSearchRecordTypeEnum.Hifo, label: 'HIFOs' },
  { key: GlobalSearchRecordTypeEnum.HifoCascade, label: 'HIFO Cascades' },
  { key: GlobalSearchRecordTypeEnum.MatchCampaign, label: 'Match Campaigns' },
  {
    key: GlobalSearchRecordTypeEnum.MatchCampaignCategory,
    label: 'Match Campaign Categories',
  },
  {
    key: GlobalSearchRecordTypeEnum.CharityCategory,
    label: 'Charity Categories',
  },
  {
    key: GlobalSearchRecordTypeEnum.CharityBankAccount,
    label: 'Bank Accounts',
  },
]

const props = defineProps<{
  modelValue: SearchFilter
  recordCounts: Record<GlobalSearchRecordTypeEnum, number>
  toolCount: number
  active: boolean
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', value: SearchFilter): void
  (event: 'update:menuOpen', value: boolean): void
}>()

const MenuOpenSync = defineComponent({
  props: {
    open: { type: Boolean, required: true },
  },
  emits: ['update:open'],
  setup(componentProps, { emit: emitOpen }) {
    watch(
      () => componentProps.open,
      (open) => emitOpen('update:open', open)
    )
    return () => null
  },
})

const menuOpen = ref(false)

watch(menuOpen, (open) => emit('update:menuOpen', open))

const filterRowRef = ref<HTMLElement | null>(null)
const measureRowRef = ref<HTMLElement | null>(null)
const measureOverflowRef = ref<HTMLElement | null>(null)
const visibleFilterChips = ref<FilterChip[]>([])
const overflowFilterChips = ref<FilterChip[]>([])
let filterRowObserver: ResizeObserver | null = null

const filterChips = computed(() => {
  const recordChips = RECORD_FILTER_CHIPS.map((chip) => ({
    ...chip,
    count: props.recordCounts[chip.key],
  })).sort((a, b) => Number(b.count > 0) - Number(a.count > 0))

  return [
    { key: 'top' as const, label: 'Top Results', count: null },
    { key: 'tools' as const, label: 'Tools', count: props.toolCount },
    ...recordChips,
  ]
})

const overflowContainsSelection = computed(() =>
  overflowFilterChips.value.some((chip) => chip.key === props.modelValue)
)

function isFilterDisabled(chip: FilterChip) {
  return chip.count === 0
}

function chipIcon(chip: FilterChip) {
  if (chip.key === 'top') return undefined
  if (chip.key === 'tools') return GLOBAL_SEARCH_TOOLS_ICON
  return globalSearchRecordTypeIcon(chip.key)
}

function updateFilterOverflow() {
  const row = filterRowRef.value
  const measureRow = measureRowRef.value
  const chips = filterChips.value

  if (!row || !measureRow) {
    visibleFilterChips.value = chips
    overflowFilterChips.value = []
    return
  }

  const chipWidths = Array.from(
    measureRow.querySelectorAll('[data-filter-chip]')
  ).map((element) => (element as HTMLElement).offsetWidth)

  const split = splitItemsForOverflow(
    chips,
    chipWidths,
    row.clientWidth,
    measureOverflowRef.value?.offsetWidth ?? 28,
    FILTER_ROW_GAP
  )

  visibleFilterChips.value = split.visible
  overflowFilterChips.value = split.overflow
}

watch(
  filterChips,
  async (chips) => {
    visibleFilterChips.value = chips
    overflowFilterChips.value = []
    await nextTick()
    updateFilterOverflow()
  },
  { immediate: true }
)

watch(
  () => props.active,
  async (active) => {
    if (!active) return
    await nextTick()
    updateFilterOverflow()
  }
)

onMounted(() => {
  window.addEventListener('resize', updateFilterOverflow)
  if (typeof ResizeObserver === 'undefined' || !filterRowRef.value) return
  filterRowObserver = new ResizeObserver(() => {
    updateFilterOverflow()
  })
  filterRowObserver.observe(filterRowRef.value)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateFilterOverflow)
  filterRowObserver?.disconnect()
})
</script>
